// src/lib/datetime.js
// One source of truth for the user's local day. The active timezone resolves
// from the user profile (or the device) and is held module-side so React
// components and plain modules (streak, schedule, etc.) all agree on it.

let _tz = null

// A zone name the runtime will actually accept. Some Android WebViews/TWAs report
// resolvedOptions().timeZone as 'Etc/Unknown', which every later
// toLocaleDateString({ timeZone }) call then throws on ("Invalid time zone
// specified: Etc/Unknown" — a live crash on / on 2026-09-15). Probe once and fall
// back rather than trusting the report.
function usableTZ(tz) {
  if (!tz || tz === 'Etc/Unknown') return null
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return tz } catch { return null }
}

export function deviceTZ() {
  try { return usableTZ(Intl.DateTimeFormat().resolvedOptions().timeZone) || 'America/New_York' }
  catch (e) { return 'America/New_York' }
}

export function setUserTZ(tz) { _tz = usableTZ(tz) }
export function getUserTZ() { return _tz || deviceTZ() }

function isBareDate(v) {
  return typeof v === 'string' && v.length === 10 && v[4] === '-' && v[7] === '-'
}

// YYYY-MM-DD for the given instant in the user timezone. A bare calendar
// date (TMDB air_date) has no zone, so it is returned unchanged.
export function dayKey(dateish) {
  if (dateish == null) dateish = new Date()
  if (isBareDate(dateish)) return dateish
  const d = (dateish instanceof Date) ? dateish : new Date(dateish)
  return d.toLocaleDateString('en-CA', { timeZone: getUserTZ() })
}

export function todayKey() { return dayKey(new Date()) }
export function isToday(dateish) { return dayKey(dateish) === todayKey() }
export function isSameDay(a, b) { return dayKey(a) === dayKey(b) }

// Whole calendar days from the user today to the target (positive = future).
export function daysUntil(dateish) {
  const a = new Date(todayKey() + 'T12:00:00')
  const b = new Date(dayKey(dateish) + 'T12:00:00')
  return Math.round((b - a) / 86400000)
}
export function daysAgo(dateish) { return -daysUntil(dateish) }

// Previous calendar day for a YYYY-MM-DD string (timezone-agnostic).
export function prevDay(ymd) {
  const p = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(p[0], p[1] - 1, p[2]))
  dt.setUTCDate(dt.getUTCDate() - 1)
  return dt.toISOString().slice(0, 10)
}

// Next calendar day for a YYYY-MM-DD string (timezone-agnostic, DST-proof).
export function nextDay(ymd) {
  const p = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(p[0], p[1] - 1, p[2] + 1))
  return dt.toISOString().slice(0, 10)
}

// Display a date. Bare calendar dates pin to local noon so they never slip a
// day; real timestamps render in the user timezone.
export function fmtDate(dateish, opts) {
  if (!dateish) return ''
  if (!opts) opts = { month: 'short', day: 'numeric', year: 'numeric' }
  const d = isBareDate(dateish) ? new Date(dateish + 'T12:00:00') : new Date(dateish)
  return d.toLocaleDateString('en-US', opts)
}

// Relative "time ago" for activity rows. Default: "just now" / "5m ago" /
// "3h ago" / "2d ago", falling back to a short date after a week.
// compact: "5m" / "3h" / "2d" (tight spots like the bell dropdown).
// (RewatchVault keeps its own long-horizon variant — months/years semantics.)
export function timeAgo(iso, { compact = false } = {}) {
  if (!iso) return ''
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (compact) {
    if (mins < 60) return `${Math.max(0, mins)}m`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h`
    return `${Math.floor(hrs / 24)}d`
  }
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
